import { useMemo } from 'react'
import {
  IntegrationServiceId,
  isBusinessEventExportEnabledForService,
  isCalendarMappingEnabledForService,
  isCustomObjectsEnabledForService,
} from '../../../../common/integrations'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { useAppSelector } from '../../../../store/hooks'
import { useGetMeQuery } from '../../../../store/user/user'
import { ContactAdminSettings } from './AdminSettingsTabs/InboundMappingSettings/ContactMappingSettings/ContactAdminSettings'
import { GeneralAdminSettings } from './AdminSettingsTabs/General/GeneralAdminSettings'
import { SessionAdminSettings } from './AdminSettingsTabs/OutboundMappingSettings/SessionMappingSettings/SessionAdminSettings'
import { TaskAdminSettings } from './AdminSettingsTabs/OutboundMappingSettings/TaskMappingSettings/TaskAdminSettings'
import { AdditionalObjectsAdminSettings } from './AdminSettingsTabs/InboundMappingSettings/AdditionalObjectsMappingSetting/AdditionalObjectsAdminSettings'
import { useZocksQuota } from '../../../../components/ScopedBlock/hooks'
import { BusinessEventsMappingSettings } from './AdminSettingsTabs/OutboundMappingSettings/BusinessEventsMappingSettings/BusinessEventsMappingSettings'
import { DocumentIntelligenceAdminSettings } from './AdminSettingsTabs/OutboundMappingSettings/DocumentMappingSettings/DocumentIntelligenceAdminSettings'
import { CustomObjectsAdminSettings } from './AdminSettingsTabs/InboundMappingSettings/CustomObjectsMappingSetting/CustomObjectsAdminSettings'
import { CalendarAdminSettings } from './AdminSettingsTabs/InboundMappingSettings/CalendarMappingSettings/CalendarAdminSettings'
import { useAccount } from '../../../../common/useAccount'
import { AccountIntegrationConfigMappingType, getAccountCRMIntegrationConfig, getMappingTypeLabel } from '../../../../models/Account'
import { IntegrationAdminSettingsCustom } from './IntegrationAdminSettingsCustom'
import { isMappingTypeLocked } from './utils'
import { type ClickNavigationSection } from '../../../../components/NavigationWithSideBar/ClickNavigation'

type AdminSettingsTab = ClickNavigationSection & { mappingType?: AccountIntegrationConfigMappingType }

export const useCustomizableAdminSettings = (serviceId: IntegrationServiceId) => {
  const accountId = useAppSelector(selectAccountId)
  const { account } = useAccount({ refetchOnMountOrArgChange: true })

  const { data: userData } = useGetMeQuery({ accountId }, { refetchOnMountOrArgChange: true })

  const hasIntegration = useMemo(() => {
    if (!userData || !userData.integrations) {
      return false
    }
    return !!userData.integrations.some((i) => i.serviceId === serviceId)
  }, [userData, serviceId])

  const hasBusinessEventExportAccess = useMemo(() => account?.effectiveConfig.businessEventExport ?? false, [account])

  const { hasQuota } = useZocksQuota()

  const hasCreateMappingQuota = hasQuota('integration.createMappingEnabled.value')
  const disableTab = useMemo(() => !hasIntegration || !hasCreateMappingQuota, [hasIntegration, hasCreateMappingQuota])

  const integrationConfig = useMemo(() => getAccountCRMIntegrationConfig(account, serviceId), [account, serviceId])
  // A fully custom account config is edited outside these tabs, so the calendar mapping is hidden there rather than disabled.
  const isCustomAccountConfig = integrationConfig?.isCustom === true

  return useMemo(() => {
    const adminSettingsTabs: AdminSettingsTab[] = [
      { id: 'settings', label: 'Settings', content: () => <GeneralAdminSettings serviceId={serviceId} showLoginUrl /> },
      {
        id: 'contact',
        label: getMappingTypeLabel(AccountIntegrationConfigMappingType.CONTACT),
        mappingType: AccountIntegrationConfigMappingType.CONTACT,
        content: () => <ContactAdminSettings serviceId={serviceId} />,
        disabled: !hasIntegration,
      },
      {
        id: 'meetingSummary',
        label: getMappingTypeLabel(AccountIntegrationConfigMappingType.SESSION),
        mappingType: AccountIntegrationConfigMappingType.SESSION,
        content: () => <SessionAdminSettings serviceId={serviceId} />,
        disabled: !hasIntegration,
      },
      {
        id: 'task',
        label: getMappingTypeLabel(AccountIntegrationConfigMappingType.TASK),
        mappingType: AccountIntegrationConfigMappingType.TASK,
        content: () => <TaskAdminSettings serviceId={serviceId} />,
        disabled: disableTab,
      },
      {
        id: 'documentIntelligence',
        label: getMappingTypeLabel(AccountIntegrationConfigMappingType.DOCUMENT_SUMMARY),
        mappingType: AccountIntegrationConfigMappingType.DOCUMENT_SUMMARY,
        content: () => <DocumentIntelligenceAdminSettings serviceId={serviceId} />,
        disabled: disableTab,
      },
      {
        id: 'additionalObjects',
        label: getMappingTypeLabel(AccountIntegrationConfigMappingType.ADDITIONAL_OBJECTS),
        mappingType: AccountIntegrationConfigMappingType.ADDITIONAL_OBJECTS,
        content: () => <AdditionalObjectsAdminSettings serviceId={serviceId} />,
        disabled: disableTab,
      },
    ]
    if (isCalendarMappingEnabledForService(serviceId) && !isCustomAccountConfig) {
      adminSettingsTabs.splice(2, 0, {
        id: 'calendar',
        label: getMappingTypeLabel(AccountIntegrationConfigMappingType.CALENDAR),
        mappingType: AccountIntegrationConfigMappingType.CALENDAR,
        content: () => <CalendarAdminSettings serviceId={serviceId} />,
        disabled: disableTab,
      })
    }
    if (isCustomObjectsEnabledForService(serviceId)) {
      adminSettingsTabs.push({
        id: 'customObjects',
        label: getMappingTypeLabel(AccountIntegrationConfigMappingType.CUSTOM_OBJECTS),
        mappingType: AccountIntegrationConfigMappingType.CUSTOM_OBJECTS,
        content: () => <CustomObjectsAdminSettings serviceId={serviceId} />,
        disabled: disableTab,
      })
    }
    if (isBusinessEventExportEnabledForService(serviceId)) {
      adminSettingsTabs.push({
        id: 'businessEvents',
        label: getMappingTypeLabel(AccountIntegrationConfigMappingType.BUSINESS_EVENTS),
        mappingType: AccountIntegrationConfigMappingType.BUSINESS_EVENTS,
        content: () => <BusinessEventsMappingSettings serviceId={serviceId} />,
        disabled: disableTab || !hasBusinessEventExportAccess,
      })
    }
    return adminSettingsTabs.map(
      ({ mappingType, ...tab }): ClickNavigationSection =>
        mappingType && isMappingTypeLocked(integrationConfig, mappingType)
          ? { ...tab, content: () => <IntegrationAdminSettingsCustom serviceId={serviceId} mappingType={mappingType} /> }
          : tab,
    )
  }, [serviceId, disableTab, hasIntegration, hasBusinessEventExportAccess, isCustomAccountConfig, integrationConfig])
}
